/**
 * Assemblage du payload de création de ticket — fonctions PURES génériques,
 * partagées widget ↔ SaaS (POST /organisation/{orgId}/ticket).
 *
 * Aucune dépendance store/Vue : chaque stack lit SON propre store puis appelle
 * ces fonctions avec des données plates. Le widget reste ainsi 100 % autonome
 * (il n'importe que du TS pur) et les deux funnels produisent un payload
 * strictement identique. Le contrat (types) vit dans `../types`.
 */
import type {
  Availability,
  Passenger,
  Place,
  Tariff,
  TicketAvailability,
  TicketContact,
  TicketCreatePayload,
  TicketPassenger,
  TicketPricingTariff,
} from '../types'

/** Passager funnel → passager du payload backend. */
export function toTicketCreatePassenger(p: Passenger): TicketPassenger {
  return {
    order: p.order,
    gender: String(p.gender ?? 'Male'),
    age: p.age,
    type: String(p.type),
    param: p.param,
    first_name: p.first_name ?? '',
    last_name: p.last_name ?? '',
    birth_date: p.birth_date,
    doc_number: p.doc_number,
    nationality: p.nationality,
  }
}

/**
 * Tarif sélectionné → entrée `params.pricing.tariffs[]` (porte le `sourceKey`
 * de direction/groupe + le `price_token` signé revérifié à la création).
 * Retourne `null` si pas de tarif — l'appelant filtre (`.filter(Boolean)`).
 */
export function toTicketPricingTariff(
  tariff: Tariff | null | undefined,
  sourceKey: string,
): TicketPricingTariff | null {
  if (!tariff) return null
  return {
    id: tariff.id,
    type: String(tariff.type ?? 'ticket').toLowerCase(),
    amount: tariff.amount,
    currency: tariff.currency,
    sourceKey,
    balance_amount: tariff.balance_amount,
    date: tariff.date,
    expiration_date: tariff.expiration_date,
    price_token: tariff.price_token,
    // Code commercial du tarif (générique) figé au search.
    fare_code: tariff.fare_code,
    // Restrictions tarifaires (code flex + droits) figées au search → doivent
    // atteindre le booking pour que le provider ré-émette EXACTEMENT le tarif
    // quoté (sinon re-tarification au fallback → prix divergent).
    restriction: tariff.restriction,
  }
}

/**
 * Place choisie normalisée pour le payload de création : `in_board.passengers`
 * réduit au DISPATCH MINIMAL — uniquement `order`.
 *
 * `in_board.passengers` n'est qu'une carte d'occupation (« quels `order`
 * occupent cette place »). L'identité (type/âge/nom/param/genre) vit dans le
 * manifeste `passengers[]` du ticket et est résolue par `order` à l'émission ;
 * la dupliquer dans `in_board` était redondant ET trompeur (age/gender y sont
 * des placeholders posés à la phase availability, ex. `age:25`/`gender:Male`
 * même pour un enfant).
 *
 * Les `order` proviennent de l'allocation backend préservée
 * (`place.in_board.passengers`) si présente — on garde donc le dispatch EXACT
 * (mêmes `order`, ex. 2,3,4,5) — sinon du découpage par groupe passé en
 * `fallbackPassengers`. On ne retire que les champs redondants.
 */
export function placeWithDispatchOrders(
  place: Place,
  fallbackPassengers: Array<{ order: number }> = [],
): Record<string, unknown> {
  const existing = place.in_board?.passengers
  const source: Array<{ order?: number | null }> =
    Array.isArray(existing) && existing.length > 0 ? existing : fallbackPassengers
  return {
    ...place,
    in_board: {
      ...(place.in_board ?? {
        adults: 0,
        babies: 0,
        childs: 0,
        has_shower: false,
        has_toilet: false,
        has_window: false,
      }),
      passengers: source.map((p) => ({ order: Number(p?.order) })),
    },
  }
}

/**
 * Availability réellement réservée → leg du tableau `availability[]` du
 * payload. `place` est déjà normalisé (cf. {@link placeWithDispatchOrders}) ou
 * vide.
 */
export function toTicketAvailabilityLeg(
  direction: 'Outward' | 'Return',
  availability: Availability,
  place: unknown[],
  meals: unknown[] = [],
): TicketAvailability {
  return {
    direction,
    id: availability.id,
    group_id: availability.group_id,
    sailing_id: availability.sailing_id,
    place,
    gateway_id: availability.gateway?.id ?? '',
    meals,
    // Nom du navire figé au search → persisté tel quel sur le ticket et lu par
    // le voucher (`availability.{dir}.ship_name`).
    ship_name: availability.ship_name,
    journey: {
      journey_id: availability.journey_id,
      depart_at: availability.depart_at,
      arrive_at: availability.arrive_at,
    },
  }
}

// ════════════════════════════════════════════════════════════════════════
//  Assemblage multi-groupes — UN ticket par sous-groupe passagers.
//
//  Règle métier (cf. découpage > 4 pax) : chaque sous-groupe = un ticket
//  distinct partageant `booking_id` / `booking_reference`. Source UNIQUE de la
//  forme du payload, consommée par le widget ET le SaaS (`saveFerryBooking`) :
//  chaque mode construit SES legs/tarifs (stratégie de place propre) puis
//  délègue l'enveloppe ici → zéro divergence de structure, zéro drift.
// ════════════════════════════════════════════════════════════════════════

/** Données d'UN sous-groupe : legs déjà construits + tarifs + prix du groupe. */
export interface GroupTicketParts {
  availability: TicketAvailability[]
  pricing: TicketPricingTariff[]
  price: number
  meals?: unknown[]
  /**
   * Passagers nommés de CE sous-groupe (dispatch `place.in_board`). Absent ⇒ on
   * retombe sur `common.passengers` (manifeste complet — cas dossier unique).
   * Renseigné = dossier séparé qui ne porte QUE ses propres passagers.
   */
  passengers?: TicketPassenger[]
}

/** Champs communs à tous les tickets du dossier (contact / véhicule / dossier). */
export interface TicketEnvelopeCommon {
  organization_id: string
  currency: string
  contact: TicketContact
  /** Manifeste complet — fallback si un sous-groupe ne porte pas SES passagers. */
  passengers: TicketPassenger[]
  vehicle: unknown | null
  /**
   * Génère le dossier (booking_id + booking_reference) d'un ticket — appelé
   * UNE FOIS PAR SOUS-GROUPE émis :
   *  - renvoyer le MÊME objet à chaque appel ⇒ dossier PARTAGÉ (1 dossier, N
   *    sous-groupes — ex. widget/bein, corrélation hôte unique) ;
   *  - renvoyer un NOUVEAU objet à chaque appel ⇒ 1 dossier INDÉPENDANT par
   *    sous-groupe (gérés séparément — ex. funnel agent SaaS).
   */
  newDossier: () => { booking_id: string; booking_reference: string }
}

/**
 * Construit un `TicketCreatePayload` par sous-groupe non vide. Le dossier
 * (booking_id/reference) est délivré par `common.newDossier()` — partagé ou
 * distinct selon l'appelant. Chaque ticket porte SES passagers (`g.passengers`,
 * sinon le manifeste commun), SES `pricing.tariffs` et son `price`.
 */
export function buildGroupTicketPayloads(
  groups: GroupTicketParts[],
  common: TicketEnvelopeCommon,
): TicketCreatePayload[] {
  return groups
    .filter((g) => g.availability.length > 0)
    .map((g, i) => {
      const dossier = common.newDossier()
      return {
        organization_id: common.organization_id,
        status: 'Init' as const,
        currency: common.currency,
        reservation_type: 'OPTION' as const,
        booking_id: dossier.booking_id,
        booking_reference: dossier.booking_reference,
        availability: g.availability,
        meals: g.meals ?? [],
        contact: common.contact,
        passengers: g.passengers ?? common.passengers,
        // Le véhicule est un actif unique du DOSSIER (une voiture physique), pas
        // d'un sous-groupe passagers : on l'attache au SEUL 1er ticket (groupe 1).
        // Le dupliquer ferait réserver/facturer le véhicule 2× chez le fournisseur
        // à l'émission (chaque ticket émettant son `vehicle`).
        vehicle: i === 0 ? common.vehicle : null,
        price: g.price,
        params: { tariffs: [], pricing: { tariffs: g.pricing } },
      }
    })
}
